/**
 * UWPFSDTITab.jsx
 * Replicates the PFS & DTI (Personal Financial Statement) tab.
 * Sections: Assets, Liabilities & Net Worth, Credit Summary,
 * PFS Summary, Debt table, Income, Living Expense Calculation.
 */
import React, { useState, useMemo } from "react";
import { cn, formatCurrency, formatPercent } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { AlertTriangle, CheckCircle, XCircle } from "lucide-react";

const CREDIT_FIELDS = [
  { key: "equifax",    label: "Equifax" },
  { key: "experian",   label: "Experian" },
  { key: "transunion", label: "TransUnion" },
];

const DEFAULT_ASSETS = {
  savingsAccount:         0, stocksBonds:            0,
  cashSurrenderValue:     0, irasRetirement:         0,
  accountsReceivable:     0, notesReceivable:        0,
  primaryResidence:       0, investmentRealEstate:   0,
  automobiles:            0, personalProperty:       0,
  closelyheldCompanies:   0, otherAssets:           0,
};

const DEFAULT_LIABILITIES = {
  accountsPayableCredit:  0, notesBanks:             0,
  notesBanksInstallment:  0, studentLoans:           0,
  lifeInsuranceLoans:     0, unpaidTaxes:            0,
  realEstateMortgages:    0, investmentMortgages:    0,
  autoInstallments:       0, otherLiabilities:      0,
};

const DEFAULT_CREDIT = {
  equifaxScore: 0, experianScore: 0, transunionScore: 0,
  equifaxDate: "", experianDate: "", transunionDate: "",
  collections: 0, currentDelinquencies: 0,
  satisfactoryAccounts: 0, openTradeAccounts: 0,
  revolvingBalance: 0, revolvingLimit: 0,
};

const DEFAULT_INCOME = {
  wages2022: 0, wages2023: 0, wages2024: 0, wagesProjected: 0,
  businessIncome2022: 0, businessIncome2023: 0, businessIncome2024: 0,
  rentalIncome: 0, dividendInterest: 0, scheduleE: 0, farmIncome: 0,
  socialSecurity: 0, retirementPension: 0, alimony: 0,
  otherIncome: 0,
};

function FieldRow({ label, reportedKey, adjustedKey, state, onChange }) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-3 py-1 text-xs text-gray-700 border-r border-gray-200 min-w-[160px]">{label}</td>
      <td className="px-1 py-0.5 border-r border-gray-200">
        <input
          type="number"
          value={state[reportedKey] || ""}
          onChange={e => onChange(reportedKey, Number(e.target.value) || 0)}
          placeholder="0"
          className="w-20 text-right font-mono text-xs bg-blue-50 border border-blue-100 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      </td>
      {adjustedKey && (
        <td className="px-1 py-0.5 border-r border-gray-200">
          <input
            type="number"
            value={state[adjustedKey] || ""}
            onChange={e => onChange(adjustedKey, Number(e.target.value) || 0)}
            placeholder="0"
            className="w-20 text-right font-mono text-xs bg-yellow-50 border border-yellow-200 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-yellow-400"
          />
        </td>
      )}
    </tr>
  );
}

function SectionHeader({ label, colSpan = 3 }) {
  return (
    <tr><td colSpan={colSpan} className="uw-row-header px-3 py-1.5 text-xs font-bold uppercase tracking-wide">{label}</td></tr>
  );
}

export default function UWPFSDTITab({ initialData, borrowerName = "0", onDataChange }) {
  const [assets,      setAssets]      = useState(initialData?.assets      || DEFAULT_ASSETS);
  const [liabilities, setLiabilities] = useState(initialData?.liabilities || DEFAULT_LIABILITIES);
  const [credit,      setCredit]      = useState(initialData?.credit      || DEFAULT_CREDIT);
  const [income,      setIncome]      = useState(initialData?.income      || DEFAULT_INCOME);
  const [numAdults,   setNumAdults]   = useState(2);
  const [numChildren, setNumChildren] = useState(2);
  const [livingExpenseOverride, setLivingExpenseOverride] = useState(false);
  const [guarantorName, setGuarantorName] = useState(borrowerName || "");
  const [spouseName,    setSpouseName]    = useState("");
  const [creditNote,    setCreditNote]    = useState("");

  const updateAssets = (key, val) => setAssets(prev => ({ ...prev, [key]: val }));
  const updateLiab   = (key, val) => setLiabilities(prev => ({ ...prev, [key]: val }));
  const updateIncome = (key, val) => setIncome(prev => ({ ...prev, [key]: val }));

  // Totals
  const totalAssets = useMemo(() => Object.values(assets).reduce((s, v) => s + (Number(v) || 0), 0), [assets]);
  const totalLiab   = useMemo(() => Object.values(liabilities).reduce((s, v) => s + (Number(v) || 0), 0), [liabilities]);
  const netWorth    = totalAssets - totalLiab;
  const avgScore    = useMemo(() => {
    const scores = [credit.equifaxScore, credit.experianScore, credit.transunionScore].map(Number).filter(s => s > 0);
    return scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  }, [credit]);

  // Living expense calculation: $12,000/adult/yr + $5,000/child/yr
  const livingExpenseCalc = (numAdults * 12000) + (numChildren * 5000);

  // Annual gross income
  const latestWages = Number(income.wagesProjected) || Number(income.wages2024) || Number(income.wages2023) || 0;
  const totalAnnualIncome = latestWages
    + (Number(income.businessIncome2024) || 0)
    + (Number(income.rentalIncome) || 0)
    + (Number(income.dividendInterest) || 0)
    + (Number(income.socialSecurity) || 0)
    + (Number(income.retirementPension) || 0);

  const totalPersonalDebtService = Object.values(liabilities).reduce((s, v) => s + (Number(v) || 0), 0);
  const dti = totalAnnualIncome > 0 ? (totalPersonalDebtService / totalAnnualIncome * 100) : 0;

  const scoreColor = (s) => {
    if (!s) return "text-gray-400";
    if (s >= 720) return "text-green-700 font-bold";
    if (s >= 660) return "text-yellow-700 font-bold";
    return "text-red-600 font-bold";
  };

  const TH = "bg-[#1e3a5f] text-white text-[10px] font-semibold px-2 py-1.5 border-r border-gray-600";

  return (
    <div className="h-full overflow-auto bg-white">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 py-2 bg-[#dce6f1] border-b border-gray-300 sticky top-0 z-20">
        <span className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wide">Personal Financial Statement & DTI</span>
        <div className="ml-auto flex items-center gap-3">
          {avgScore > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-600">Avg Credit Score:</span>
              <span className={cn("text-base", scoreColor(avgScore))}>{avgScore}</span>
            </div>
          )}
          {dti > 0 && (
            <div className={cn("px-3 py-1 rounded border text-xs font-bold",
              dti <= 35 ? "bg-green-100 text-green-800 border-green-300" :
              dti <= 50 ? "bg-yellow-100 text-yellow-800 border-yellow-300" :
                          "bg-red-100 text-red-800 border-red-300"
            )}>
              DTI: {dti.toFixed(1)}%
            </div>
          )}
        </div>
      </div>

      {/* Credit warning banner */}
      {creditNote && (
        <div className="mx-4 mt-3 flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-400 rounded-md text-xs text-yellow-800">
          <AlertTriangle size={14} className="text-yellow-500 shrink-0" />
          {creditNote}
        </div>
      )}

      <div className="p-4">
        {/* Guarantor row */}
        <div className="flex items-center gap-6 mb-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 font-semibold">Personal Financial Statement of:</span>
            <input value={guarantorName} onChange={e => setGuarantorName(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-[180px]"
              placeholder="Guarantor name" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Spouse:</span>
            <input value={spouseName} onChange={e => setSpouseName(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-[140px]"
              placeholder="(if applicable)" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* ── Assets ── */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-[#1e3a5f] text-white text-xs font-bold px-3 py-2">Assets</div>
            <table className="w-full border-collapse text-xs">
              <thead><tr>
                <th className={TH + " text-left"}>Asset</th>
                <th className={TH}>Reported</th>
                <th className={TH}>Adjusted</th>
              </tr></thead>
              <tbody>
                {[
                  ["Savings Account",           "savingsAccount",       "savingsAccountAdj"],
                  ["Stocks & Bonds",             "stocksBonds",          "stocksBondsAdj"],
                  ["Cash Surrender Value Life Ins.", "cashSurrenderValue","cashSurrenderAdj"],
                  ["IRAs & Other Retirement",    "irasRetirement",       "irasRetirementAdj"],
                  ["Accounts & Notes Receivable","accountsReceivable",   "accountsReceivableAdj"],
                  ["Notes Receivable",           "notesReceivable",      "notesReceivableAdj"],
                  ["Primary Residence",          "primaryResidence",     "primaryResidenceAdj"],
                  ["Investment & Other Real Estate","investmentRealEstate","investmentRealEstateAdj"],
                  ["Automobiles",                "automobiles",          "automobilesAdj"],
                  ["Personal Property",          "personalProperty",     "personalPropertyAdj"],
                  ["Closely-held Companies",     "closelyheldCompanies", "closelyheldAdj"],
                  ["Other Assets",               "otherAssets",          "otherAssetsAdj"],
                ].map(([label, key, adjKey]) => (
                  <FieldRow key={key} label={label} reportedKey={key} adjustedKey={adjKey} state={assets} onChange={updateAssets} />
                ))}
                <tr className="uw-subtotal-row">
                  <td className="px-3 py-1.5 text-xs font-bold bg-[#c6d9f1] text-[#1e3a5f]">Total Assets</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-[#1e3a5f]">{formatCurrency(totalAssets)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-gray-400">$0</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Liabilities ── */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-[#1e3a5f] text-white text-xs font-bold px-3 py-2">Liabilities & Net Worth</div>
            <table className="w-full border-collapse text-xs">
              <thead><tr>
                <th className={TH + " text-left"}>Liability</th>
                <th className={TH}>Reported</th>
                <th className={TH}>Adjusted</th>
              </tr></thead>
              <tbody>
                {[
                  ["Accounts Payable & Credit Cards", "accountsPayableCredit","acctPayAdj"],
                  ["Notes Payable to Banks",           "notesBanks",           "notesBanksAdj"],
                  ["Installment Accounts (Other)",     "notesBanksInstallment","installOtherAdj"],
                  ["Installment Accounts (Student Loans)","studentLoans",      "studentLoansAdj"],
                  ["Loans Against Life Insurance (LSLI)","lifeInsuranceLoans","lifeInsAdj"],
                  ["Unpaid Taxes",                     "unpaidTaxes",          "unpaidTaxesAdj"],
                  ["Real Estate Mortgages",            "realEstateMortgages",  "reMortgagesAdj"],
                  ["Investment & Other Real Estate Mtgs","investmentMortgages","investMtgAdj"],
                  ["Installment Accounts (Auto)",      "autoInstallments",     "autoInstAdj"],
                  ["All Other Liabilities",            "otherLiabilities",     "otherLiabAdj"],
                ].map(([label, key, adjKey]) => (
                  <FieldRow key={key} label={label} reportedKey={key} adjustedKey={adjKey} state={liabilities} onChange={updateLiab} />
                ))}
                <tr className="uw-subtotal-row">
                  <td className="px-3 py-1.5 text-xs font-bold bg-[#c6d9f1] text-[#1e3a5f]">Total Liabilities</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-[#1e3a5f]">{formatCurrency(totalLiab)}</td>
                  <td></td>
                </tr>
                <tr className="uw-total-row">
                  <td className="px-3 py-1.5 text-xs font-bold bg-[#1e3a5f] text-white">Total Net Worth</td>
                  <td className={cn("px-2 py-1.5 text-right font-mono font-bold", netWorth < 0 ? "text-red-300" : "text-white")}>
                    {formatCurrency(netWorth)}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td className="px-3 py-1.5 text-xs font-bold text-[#1e3a5f]">Total Liabilities & Net Worth</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-gray-800">{formatCurrency(totalAssets)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Credit Summary ── */}
          <div className="space-y-3">
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-[#1e3a5f] text-white text-xs font-bold px-3 py-2">Credit Summary — Guarantor</div>
              <div className="p-3 space-y-2 text-xs">
                {/* Credit bureau scores */}
                {["Equifax","Experian","TransUnion"].map((bureau, i) => {
                  const scoreKey = `${bureau.toLowerCase()}Score`;
                  const dateKey  = `${bureau.toLowerCase()}Date`;
                  const score    = Number(credit[scoreKey]) || 0;
                  return (
                    <div key={bureau} className="flex items-center gap-2">
                      <span className="w-24 text-gray-600">{bureau}</span>
                      <input type="date" value={credit[dateKey] || ""}
                        onChange={e => setCredit(p => ({ ...p, [dateKey]: e.target.value }))}
                        className="flex-1 text-xs border border-gray-200 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <input type="number" value={score || ""}
                        onChange={e => setCredit(p => ({ ...p, [scoreKey]: Number(e.target.value) || 0 }))}
                        placeholder="Score"
                        className={cn("w-16 text-right font-mono text-xs border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400",
                          score >= 720 ? "bg-green-50 border-green-200" :
                          score >= 660 ? "bg-yellow-50 border-yellow-200" :
                          score > 0    ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-100"
                        )}
                      />
                    </div>
                  );
                })}
                <div className="flex justify-between font-bold text-[#1e3a5f] border-t pt-1">
                  <span>Average Score</span>
                  <span className={cn("text-base", scoreColor(avgScore))}>{avgScore || "—"}</span>
                </div>

                <div className="border-t pt-2 mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                  {[
                    ["Collections",           "collections"],
                    ["Open Trade Accounts",   "openTradeAccounts"],
                    ["Current Delinquencies", "currentDelinquencies"],
                    ["Satisfactory Accounts", "satisfactoryAccounts"],
                    ["Revolving Balance",     "revolvingBalance"],
                    ["Revolving Limit",       "revolvingLimit"],
                  ].map(([label, key]) => (
                    <div key={key} className="flex justify-between items-center gap-1">
                      <span className="text-gray-600 text-[10px]">{label}</span>
                      <input type="number" value={credit[key] || ""}
                        onChange={e => setCredit(p => ({ ...p, [key]: Number(e.target.value) || 0 }))}
                        className="w-16 text-right font-mono text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  ))}
                </div>

                {/* Utilization */}
                {credit.revolvingLimit > 0 && (
                  <div className="flex justify-between text-[10px] border-t pt-1">
                    <span className="text-gray-600">Utilization Rate:</span>
                    <span className={cn("font-mono font-bold",
                      (credit.revolvingBalance / credit.revolvingLimit) > 0.5 ? "text-red-600" : "text-green-700"
                    )}>
                      {formatPercent((credit.revolvingBalance / credit.revolvingLimit) * 100)}
                    </span>
                  </div>
                )}

                {/* Credit note */}
                <div className="border-t pt-2">
                  <input
                    value={creditNote}
                    onChange={e => setCreditNote(e.target.value)}
                    placeholder="Credit review note (e.g. 'Review credit report for fraud alerts')"
                    className="w-full text-[10px] border border-yellow-300 bg-yellow-50 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                  />
                </div>
              </div>
            </div>

            {/* Living Expense Calc */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-[#1e3a5f] text-white text-xs font-bold px-3 py-2">Living Expense Calculation</div>
              <div className="p-3 space-y-2 text-xs">
                <div className="grid grid-cols-3 text-[10px] font-semibold text-gray-500 border-b pb-1">
                  <span></span><span className="text-center">Count</span><span className="text-right">Annual Amount</span>
                </div>
                <div className="grid grid-cols-3 items-center">
                  <span className="text-gray-700"># of Adults</span>
                  <input type="number" min={0} value={numAdults}
                    onChange={e => setNumAdults(Number(e.target.value) || 0)}
                    className="w-full text-center border border-gray-200 rounded px-2 py-0.5 bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <span className="text-right font-mono text-gray-800">{formatCurrency(numAdults * 12000)}</span>
                </div>
                <div className="grid grid-cols-3 items-center">
                  <span className="text-gray-700"># of Children</span>
                  <input type="number" min={0} value={numChildren}
                    onChange={e => setNumChildren(Number(e.target.value) || 0)}
                    className="w-full text-center border border-gray-200 rounded px-2 py-0.5 bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <span className="text-right font-mono text-gray-800">{formatCurrency(numChildren * 5000)}</span>
                </div>
                <div className="flex justify-between font-bold text-[#1e3a5f] border-t pt-1">
                  <span>Total Calc. Living Expense</span>
                  <span className="font-mono">{formatCurrency(livingExpenseCalc)}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-500">
                  <span>Override:</span>
                  <button onClick={() => setLivingExpenseOverride(v => !v)}
                    className={cn("px-2 py-0.5 rounded border text-[10px] font-bold", livingExpenseOverride ? "bg-[#1e3a5f] text-white" : "bg-white text-gray-700 border-gray-300")}>
                    {livingExpenseOverride ? "Yes" : "No"}
                  </button>
                  <span className="ml-1 text-[9px] italic">10% of AGI, whichever is greater</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Income Table ── */}
        <div className="mt-4 border border-gray-300 rounded-lg overflow-hidden">
          <div className="bg-[#1e3a5f] text-white text-xs font-bold px-3 py-2">Income (Guarantor)</div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-[#dce6f1] text-[#1e3a5f]">
                  <th className="px-3 py-1.5 text-left font-semibold min-w-[200px] border-r border-gray-300">Income Source</th>
                  <th className="px-3 py-1.5 text-right font-semibold border-r border-gray-300 min-w-[90px]">2022</th>
                  <th className="px-3 py-1.5 text-right font-semibold border-r border-gray-300 min-w-[90px]">2023</th>
                  <th className="px-3 py-1.5 text-right font-semibold border-r border-gray-300 min-w-[90px]">2024</th>
                  <th className="px-3 py-1.5 text-right font-semibold min-w-[90px]">Projected</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Wages / Salaries, Tips, etc.",   keys: ["wages2022","wages2023","wages2024","wagesProjected"] },
                  { label: "Interest, Dividends, etc.",       keys: [null,null,null,"dividendInterest"] },
                  { label: "Income from Rental / Income, etc.", keys: [null,null,null,"rentalIncome"] },
                  { label: "Alimony Received",               keys: [null,null,null,"alimony"] },
                  { label: "Business Income (Net – Net)",     keys: ["businessIncome2022","businessIncome2023","businessIncome2024",null] },
                  { label: "Capital & Other Gains (Losses)",  keys: [null,null,null,null] },
                  { label: "IRA Premiums / Distributions",    keys: [null,null,null,null] },
                  { label: "Farm Income",                     keys: [null,null,null,"farmIncome"] },
                  { label: "Social Security & Disability",   keys: [null,null,null,"socialSecurity"] },
                  { label: "Rental Property Income (Net)",   keys: [null,null,null,null] },
                  { label: "Partnerships & Distributions, etc.", keys: [null,null,null,null] },
                  { label: "S-Corp Distributions (+/−)",    keys: [null,null,null,null] },
                ].map(({ label, keys }) => (
                  <tr key={label} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-1 text-gray-700 border-r border-gray-200">{label}</td>
                    {keys.map((key, i) => (
                      <td key={i} className="px-1 py-0.5 border-r border-gray-200">
                        {key ? (
                          <input type="number" value={income[key] || ""}
                            onChange={e => updateIncome(key, Number(e.target.value) || 0)}
                            placeholder="0"
                            className="w-full text-right font-mono text-xs bg-blue-50 border border-blue-100 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 tabular-nums"
                          />
                        ) : (
                          <span className="block w-full text-right font-mono text-xs text-gray-300 px-2">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Totals */}
                <tr className="border-t-2 border-[#1e3a5f] bg-[#dce6f1]">
                  <td className="px-3 py-1.5 text-xs font-bold text-[#1e3a5f] border-r border-gray-300">Business Cash Available</td>
                  {[0,0,0,totalAnnualIncome].map((v, i) => (
                    <td key={i} className={cn("px-2 py-1.5 text-right font-mono text-xs font-bold tabular-nums border-r border-gray-300", v !== 0 ? "text-[#1e3a5f]" : "text-gray-300")}>
                      {v !== 0 ? formatCurrency(v) : ""}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── DTI Summary Panel ── */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          {[
            { label: "Total Assets",             value: totalAssets,            color: "text-green-700" },
            { label: "Total Liabilities",        value: totalLiab,              color: "text-red-600" },
            { label: "Net Worth",                value: netWorth,               color: netWorth < 0 ? "text-red-600" : "text-green-700" },
            { label: "Annual Gross Income",      value: totalAnnualIncome,      color: "text-green-700" },
            { label: "Annual Debt Service",      value: totalPersonalDebtService * 12, color: "text-gray-700" },
            { label: "Living Expense (Annual)",  value: livingExpenseCalc,      color: "text-gray-700" },
          ].map(({ label, value, color }) => (
            <div key={label} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">{label}</div>
              <div className={cn("text-lg font-bold font-mono tabular-nums", color)}>
                {formatCurrency(value)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
